import { useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { CartUser as User } from '../types/cart.types';
import { cartApi, type CheckoutDraftPayload } from '../api/cart.api';
import { persistCart, readStoredCart } from '../../../lib/cartStorage';
import { clearCheckoutDraft, persistCheckoutDraft, readCheckoutDraft } from '../../../lib/checkoutDraftStorage';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { normalizeCartLines } from '../utils/cartNormalization';
import { recordClientDiagnostic } from '../../../lib/clientDiagnostics';

interface useCartHooksParams {
  currentUser: User | null;
  setCart: Dispatch<
    SetStateAction<{ id: string; productId?: string; quantity: number; temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either' }[]>
  >;
}

type CartItem = {
  id?: string;
  productId?: string;
  quantity: number;
  temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either';
};

const cartFingerprint = (items: CartItem[]): string =>
  JSON.stringify(
    normalizeCartLines(items).map(({ id, quantity, temperaturePreference }) => [
      id,
      quantity,
      temperaturePreference
    ])
  );

const toStateLines = (items: CartItem[]) =>
  normalizeCartLines(items).map(({ id, productId, quantity, temperaturePreference }) => ({
    id,
    productId,
    quantity,
    temperaturePreference
  }));

export const useCartHooks = ({ currentUser, setCart }: useCartHooksParams) => {
  const cartVersionRef = useRef<number>(0);
  /** Serialize puts so pagehide + debounced persistence cannot race on cartVersion. */
  const syncChainRef = useRef<Promise<void>>(Promise.resolve());
  /** Last payload successfully written (or adopted from server) — skip no-op re-puts. */
  const lastSyncedFingerprintRef = useRef<string>('');
  /**
   * When true, the next persistence effect must not PUT — cart state came from
   * server adopt / conflict recovery and re-syncing would restart a 409 storm.
   */
  const skipNextPersistSyncRef = useRef(false);

  const adoptServerCart = useCallback(
    async () => {
      const latest = await cartApi.get();
      cartVersionRef.current = latest.cartVersion;
      const serverLines = toStateLines(latest.items || []);
      lastSyncedFingerprintRef.current = cartFingerprint(serverLines);
      skipNextPersistSyncRef.current = true;
      setCart(serverLines);
      persistCart(serverLines);
    },
    [setCart]
  );

  const syncCartToServer = useCallback(
    async (items: CartItem[], options?: { keepalive?: boolean; force?: boolean }): Promise<boolean> => {
      if (!currentUser) return false;

      if (skipNextPersistSyncRef.current && !options?.force) {
        skipNextPersistSyncRef.current = false;
        lastSyncedFingerprintRef.current = cartFingerprint(items);
        return true;
      }

      const desired = normalizeCartLines(items);
      const fingerprint = cartFingerprint(desired);
      if (!options?.force && fingerprint === lastSyncedFingerprintRef.current) {
        return true;
      }

      const run = async (): Promise<boolean> => {
        const putDesired = async (version: number) => {
          const { cartVersion } = await cartApi.put(
            desired,
            buildIdempotencyKey('cart-put', [currentUser._id]),
            version,
            { keepalive: options?.keepalive }
          );
          cartVersionRef.current = cartVersion;
          lastSyncedFingerprintRef.current = fingerprint;
        };

        try {
          await putDesired(cartVersionRef.current);
          return true;
        } catch (err: unknown) {
          const errObj = (typeof err === 'object' && err !== null) ? (err as Record<string, unknown>) : null;
          if (errObj?.status === 409 || errObj?.code === 'CART_VERSION_CONFLICT') {
            try {
              // Refresh version, then retry the *intended* client cart once.
              // Do NOT concatenate server+client lines — normalizeCartLines sums
              // quantities and that created an infinite grow/409/rate-limit loop.
              const latest = await cartApi.get();
              cartVersionRef.current = latest.cartVersion;
              await putDesired(cartVersionRef.current);
              // Keep local UI on the intended cart we just wrote.
              skipNextPersistSyncRef.current = true;
              setCart(
                desired.map(({ id, productId, quantity, temperaturePreference }) => ({
                  id,
                  productId,
                  quantity,
                  temperaturePreference
                }))
              );
              persistCart(desired);
              return true;
            } catch (retryErr: unknown) {
              // Concurrent flush still conflicting — adopt server and stop.
              try {
                await adoptServerCart();
              } catch {
                recordClientDiagnostic('[syncCartToServer] Conflict recovery failed', { error: String(retryErr) }, 'warning');
              }
              return false;
            }
          }
          if (errObj?.status === 429) {
            // Back off: adopt server snapshot so persistence stops hammering.
            try {
              await adoptServerCart();
            } catch {
              recordClientDiagnostic('[syncCartToServer] Rate limited; could not adopt server cart', { error: String(err) }, 'warning');
            }
            return false;
          }

          // If some items are invalid or their mapped store is closed, prune them and retry
          const details = (errObj?.details && typeof errObj.details === 'object') ? (errObj.details as Record<string, unknown>) : null;
          const missing = Array.isArray(details?.missingProductIds) ? (details.missingProductIds as unknown[]) : [];
          const closed = Array.isArray(details?.productIds) ? (details.productIds as unknown[]) : [];
          const badIds = new Set<string>();
          for (const item of [...missing, ...closed]) {
            if (typeof item === 'string' && item.trim()) badIds.add(item.trim());
          }
          if (badIds.size > 0) {
            const pruned = desired.filter(
              (line) => !badIds.has(String(line.productId || line.id || '').trim())
            );
            if (pruned.length > 0) {
              try {
                const { cartVersion } = await cartApi.put(
                  pruned,
                  buildIdempotencyKey('cart-put-pruned', [currentUser._id]),
                  cartVersionRef.current,
                  { keepalive: options?.keepalive }
                );
                cartVersionRef.current = cartVersion;
                lastSyncedFingerprintRef.current = cartFingerprint(pruned);
                skipNextPersistSyncRef.current = true;
                const prunedLines = toStateLines(pruned);
                setCart(prunedLines);
                persistCart(prunedLines);
                return true;
              } catch {
                // Fall back to adopting server snapshot
              }
            }
          }

          // Non-conflict failures: never leave optimistic local cart as "truth".
          recordClientDiagnostic('[syncCartToServer] Failed', { error: String(err) }, 'warning');
          try {
            await adoptServerCart();
          } catch {
            recordClientDiagnostic('[syncCartToServer] Could not adopt server cart after sync failure', { error: String(err) }, 'warning');
          }
          return false;
        }
      };
      const next = syncChainRef.current.then(run, run);
      syncChainRef.current = next.then(
        () => undefined,
        () => undefined
      );
      return await next;
    },
    [adoptServerCart, currentUser, setCart]
  );

  const loadCartFromServer = useCallback(
    async (options: { mergeGuestCart?: boolean; overwriteLocal?: boolean } = {}) => {
      if (!currentUser) return;
      const userId = String(currentUser.id || currentUser._id || '');
      try {
        const data = await cartApi.get();
        cartVersionRef.current = data.cartVersion;

        const localCart = options.overwriteLocal ? [] : readStoredCart();
        let normalizedForState: {
          id: string;
          quantity: number;
          temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either';
        }[];

        if (options.mergeGuestCart) {
          const merged = toStateLines([...localCart, ...data.items]);
          const synced = await syncCartToServer(merged, { force: true });
          if (synced) {
            normalizedForState = merged;
            setCart(normalizedForState);
            persistCart(normalizedForState);
          } else {
            normalizedForState = toStateLines(data.items);
          }
        } else if (data.items.length > 0) {
          normalizedForState = toStateLines(data.items);
          lastSyncedFingerprintRef.current = cartFingerprint(normalizedForState);
          skipNextPersistSyncRef.current = true;
          setCart(normalizedForState);
          persistCart(normalizedForState);
        } else if (localCart.length > 0) {
          normalizedForState = toStateLines(localCart);
          const synced = await syncCartToServer(normalizedForState, { force: true });
          if (synced) {
            setCart(normalizedForState);
            persistCart(normalizedForState);
          }
        } else {
          normalizedForState = [];
          lastSyncedFingerprintRef.current = cartFingerprint([]);
          skipNextPersistSyncRef.current = true;
          setCart(normalizedForState);
          persistCart(normalizedForState);
        }

        const localDraft = readCheckoutDraft(userId);
        const guestDraft = readCheckoutDraft('guest');
        const serverDraft = (data.checkoutDraft ?? {}) as CheckoutDraftPayload;
        const mergedDraft = { ...guestDraft, ...localDraft, ...serverDraft };
        if (Object.keys(guestDraft || {}).length > 0) {
          clearCheckoutDraft('guest');
        }
        if (Object.keys(mergedDraft).length > 0) {
          persistCheckoutDraft(userId, mergedDraft);
          if (Object.keys(serverDraft).length === 0 && Object.keys(localDraft).length > 0) {
            // Explicitly pick only schema-known fields — same as useCheckoutDraftPersistence —
            // to guard against stale localStorage keys from old client versions.
            const backendDraft: import('../api/cart.api').CheckoutDraftPayload = {
              ...(localDraft.shippingAddress !== undefined && { shippingAddress: localDraft.shippingAddress }),
              ...(localDraft.acceptedPolicies !== undefined && { acceptedPolicies: localDraft.acceptedPolicies }),
              ...(localDraft.deliveryInstructions !== undefined && { deliveryInstructions: localDraft.deliveryInstructions }),
              ...(localDraft.savedAddressId !== undefined && { savedAddressId: localDraft.savedAddressId }),
              ...(localDraft.savedPaymentMethodId !== undefined && { savedPaymentMethodId: localDraft.savedPaymentMethodId }),
              ...(localDraft.requestedDeliveryWindowId !== undefined && { requestedDeliveryWindowId: localDraft.requestedDeliveryWindowId }),
              ...(localDraft.tipCents !== undefined && { tipCents: Math.round(localDraft.tipCents) }),
              ...(localDraft.applyWalletBalance !== undefined && { applyWalletBalance: localDraft.applyWalletBalance }),
              ...(localDraft.promoCode !== undefined && { promoCode: localDraft.promoCode }),
              ...(localDraft.useCashPayout !== undefined && { useCashPayout: localDraft.useCashPayout }),
            };
            void cartApi.patchCheckoutDraft(backendDraft, { cartVersion: cartVersionRef.current }).catch(() => undefined);
          }
        }
      } catch (err) {
        recordClientDiagnostic('[loadCartFromServer] Failed', { error: String(err) }, 'warning');
      }
    },
    [currentUser, setCart, syncCartToServer]
  );

  const clearCart = useCallback(async () => {
    const userId = currentUser ? String(currentUser.id || currentUser._id || '') : null;
    if (userId) {
      try {
        const { cartVersion } = await cartApi.clear(cartVersionRef.current);
        cartVersionRef.current = cartVersion;
      } catch (err) {
        // Do not wipe local cart on unconfirmed server clear — avoids false-empty UX.
        recordClientDiagnostic('[clearCart] Failed to sync to server', { error: String(err) }, 'warning');
        return;
      }
    } else {
      cartVersionRef.current = 0;
    }
    setCart([]);
    persistCart([]);
    lastSyncedFingerprintRef.current = cartFingerprint([]);
    clearCheckoutDraft(userId);
    clearCheckoutDraft(null);
  }, [currentUser, setCart]);

  const applyServerCartSnapshot = useCallback(
    (payload: { items?: CartItem[]; cartVersion?: unknown }) => {
      if (Number.isFinite(Number(payload.cartVersion))) {
        cartVersionRef.current = Number(payload.cartVersion);
      }
      if (!Array.isArray(payload.items)) return;
      const normalizedForState = toStateLines(payload.items);
      lastSyncedFingerprintRef.current = cartFingerprint(normalizedForState);
      skipNextPersistSyncRef.current = true;
      setCart(normalizedForState);
      persistCart(normalizedForState);
    },
    [setCart]
  );

  const getCartVersion = useCallback(() => cartVersionRef.current, []);

  return { syncCartToServer, loadCartFromServer, clearCart, applyServerCartSnapshot, getCartVersion };
};
